import { createContext, useCallback, useContext, useEffect, useRef, useState, ReactNode } from 'react';
import api from '../src/api';
import { SessionTimeoutWarningModal } from './session-timeout-warning-modal';

interface User {
  id: string;
  email: string;
  name: string | null;
  isSuperAdmin: boolean;
  permissions?: string[];
  globalPermissions?: string[];
  assignedEventIds?: string[];
  permissionsByEvent?: Record<string, string[]>;
}

interface AuthContextType {
  isAuthenticated: boolean;
  isLoading: boolean;
  user: User | null;
  login: (userData: User) => void;
  logout: () => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

const DEFAULT_TIMEOUT_MINUTES = 15;
const WARNING_SECONDS = 60;
const PASSIVE_HEARTBEAT_INTERVAL_MS = 30_000;
const MOUSE_MOVE_MIN_DELTA_PX = 8;
const MOUSE_MOVE_ACTIVITY_THROTTLE_MS = 1_500;

function parseTimeoutMinutes(value: unknown): number {
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) return DEFAULT_TIMEOUT_MINUTES;
  return Math.min(Math.max(Math.trunc(numeric), 2), 1440);
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  const [sessionTimeoutMinutes, setSessionTimeoutMinutes] = useState(DEFAULT_TIMEOUT_MINUTES);
  const [warningOpen, setWarningOpen] = useState(false);
  const [remainingSeconds, setRemainingSeconds] = useState(WARNING_SECONDS);
  const [isRefreshing, setIsRefreshing] = useState(false);

  const lastLocalActivityRef = useRef<number>(Date.now());
  const lastPassiveHeartbeatRef = useRef<number>(0);
  const passiveHeartbeatInFlightRef = useRef(false);
  const lastMousePositionRef = useRef<{ x: number; y: number } | null>(null);
  const lastSignificantMouseActivityRef = useRef<number>(0);

  const resetLocalTimer = useCallback(
    (source: 'ui' | 'api') => {
      if (!isAuthenticated) return;
      if (warningOpen && source === 'ui') return;
      lastLocalActivityRef.current = Date.now();
      if (warningOpen) {
        setWarningOpen(false);
        setRemainingSeconds(WARNING_SECONDS);
      }
    },
    [isAuthenticated, warningOpen],
  );

  const logout = useCallback(async () => {
    setIsAuthenticated(false);
    setUser(null);
    setWarningOpen(false);

    try {
      await api.post('/admin-auth/logout');
    } catch (error) {
      console.error('Failed to logout on server:', error);
    }
  }, []);

  const handleContinueSession = useCallback(async () => {
    if (isRefreshing) return;
    setIsRefreshing(true);

    try {
      await api.post('/admin-auth/refresh');
      lastLocalActivityRef.current = Date.now();
      setWarningOpen(false);
      setRemainingSeconds(WARNING_SECONDS);
      // Notificar al debug panel que la sesión se extendió
      window.dispatchEvent(new CustomEvent('session:refreshed'));
    } catch (error: any) {
      if (error?.response?.status === 401) {
        await logout();
      } else {
        console.error('No se pudo refrescar la sesion:', error);
      }
    } finally {
      setIsRefreshing(false);
    }
  }, [isRefreshing, logout]);

  const triggerPassiveHeartbeat = useCallback(() => {
    if (!isAuthenticated) return;
    if (warningOpen) return;
    if (passiveHeartbeatInFlightRef.current) return;

    const now = Date.now();
    if (now - lastPassiveHeartbeatRef.current < PASSIVE_HEARTBEAT_INTERVAL_MS) return;

    passiveHeartbeatInFlightRef.current = true;
    lastPassiveHeartbeatRef.current = now;

    void api
      .post('/admin-auth/refresh', {}, { headers: { 'X-Passive-Request': 'true' } })
      .then(() => {
        lastLocalActivityRef.current = Date.now();
        window.dispatchEvent(new CustomEvent('session:refreshed'));
      })
      .catch(async (error: any) => {
        if (error?.response?.status === 401) {
          await logout();
        } else {
          console.error('No se pudo refrescar la sesion (passive):', error);
        }
      })
      .finally(() => {
        passiveHeartbeatInFlightRef.current = false;
      });
  }, [isAuthenticated, warningOpen, logout]);

  useEffect(() => {
    const checkAuthStatus = async () => {
      try {
        const response = await api.get('/admin-auth/me');
        if (response.status === 200) {
          setIsAuthenticated(true);
          setUser(response.data.user);
        } else {
          setIsAuthenticated(false);
          setUser(null);
        }
      } catch {
        setIsAuthenticated(false);
        setUser(null);
      } finally {
        setIsLoading(false);
      }
    };

    checkAuthStatus();
  }, []);

  useEffect(() => {
    if (!isAuthenticated) return;

    const loadSessionTimeout = async () => {
      try {
        const response = await api.get('/public/settings');
        const timeout = parseTimeoutMinutes(response.data?.['security.session.timeoutMinutes']);
        setSessionTimeoutMinutes(timeout);
      } catch (error) {
        console.error('No se pudo cargar security.session.timeoutMinutes:', error);
        setSessionTimeoutMinutes(DEFAULT_TIMEOUT_MINUTES);
      }
    };

    loadSessionTimeout();
  }, [isAuthenticated]);

  useEffect(() => {
    if (!isAuthenticated) return;
    lastLocalActivityRef.current = Date.now();
    setWarningOpen(false);
    setRemainingSeconds(WARNING_SECONDS);
  }, [isAuthenticated]);

  useEffect(() => {
    if (!isAuthenticated) return;

    const onMouseMove = (event: MouseEvent) => {
      const now = Date.now();
      const previous = lastMousePositionRef.current;
      const current = { x: event.clientX, y: event.clientY };
      lastMousePositionRef.current = current;

      if (previous) {
        const movementDelta = Math.abs(current.x - previous.x) + Math.abs(current.y - previous.y);
        const throttleWindowOpen = now - lastSignificantMouseActivityRef.current < MOUSE_MOVE_ACTIVITY_THROTTLE_MS;
        if (movementDelta < MOUSE_MOVE_MIN_DELTA_PX || throttleWindowOpen) {
          return;
        }
      }

      lastSignificantMouseActivityRef.current = now;
      resetLocalTimer('ui');
      triggerPassiveHeartbeat();
    };
    const onScroll = () => resetLocalTimer('ui');
    const onTouchStart = () => resetLocalTimer('ui');
    const onClick = () => {
      resetLocalTimer('ui');
      triggerPassiveHeartbeat();
    };
    const onKeyDown = () => {
      resetLocalTimer('ui');
      triggerPassiveHeartbeat();
    };

    window.addEventListener('mousemove', onMouseMove, { passive: true });
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('touchstart', onTouchStart, { passive: true });
    window.addEventListener('click', onClick, { passive: true });
    window.addEventListener('keydown', onKeyDown);

    return () => {
      window.removeEventListener('mousemove', onMouseMove);
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('touchstart', onTouchStart);
      window.removeEventListener('click', onClick);
      window.removeEventListener('keydown', onKeyDown);
    };
  }, [isAuthenticated, resetLocalTimer, triggerPassiveHeartbeat]);

  useEffect(() => {
    if (!isAuthenticated) return;

    const timeoutSeconds = sessionTimeoutMinutes * 60;
    const warningStartSeconds = Math.max(timeoutSeconds - WARNING_SECONDS, 0);

    const tick = () => {
      const elapsedSeconds = Math.floor((Date.now() - lastLocalActivityRef.current) / 1000);
      const secondsLeft = timeoutSeconds - elapsedSeconds;
      const shouldShowWarning = elapsedSeconds >= warningStartSeconds;

      if (secondsLeft <= 0) {
        void logout();
        return;
      }

      setWarningOpen((prev) => {
        if (shouldShowWarning && !prev) return true;
        if (!shouldShowWarning && prev) return false;
        return prev;
      });

      setRemainingSeconds(shouldShowWarning ? Math.max(Math.min(secondsLeft, WARNING_SECONDS), 1) : WARNING_SECONDS);
    };

    const intervalId = window.setInterval(tick, 1000);
    return () => window.clearInterval(intervalId);
  }, [isAuthenticated, sessionTimeoutMinutes, logout]);

  const login = (userData: User) => {
    setIsAuthenticated(true);
    setUser(userData);
    lastLocalActivityRef.current = Date.now();
  };

  return (
    <AuthContext.Provider value={{ isAuthenticated, isLoading, user, login, logout }}>
      {children}
      <SessionTimeoutWarningModal
        open={isAuthenticated && warningOpen}
        remainingSeconds={remainingSeconds}
        isRefreshing={isRefreshing}
        onContinue={handleContinueSession}
        onLogout={() => void logout()}
      />
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
